/**
 * PositionUtil - I format and derive display values from Position data
 * ONLY concerned with position display transformations - no API calls, no state management
 */
import { WeeklyTimeUnits, PositionsConst } from '@axiom/const';
import type { Candidate, Position } from '@axiom/validation';

import { PluralizeUtil } from './pluralize-util';

const monthGroups = {
  ZERO: '0-3 months',
  FOUR: '4-6 months',
  SEVEN: '7-9 months',
  TEN: '10-12 months',
  OVER_TWELVE: '12+ months',
};

const hoursPerWeekGroups = {
  ONE: '1-5 hours',
  SIX: '6-10 hours',
  ELEVEN: '11-15 hours',
  SIXTEEN: '16-20 hours',
  TWENTY_ONE: '21-25 hours',
  TWENTY_SIX: '26-30 hours',
  THIRTY_ONE: '31-35 hours',
  THIRTY_SIX: '36-40 hours',
  OVER_FORTY: '40+ hours',
};

const hoursPerWeekThresholds: Array<[number, string]> = [
  [5, hoursPerWeekGroups.ONE],
  [10, hoursPerWeekGroups.SIX],
  [15, hoursPerWeekGroups.ELEVEN],
  [20, hoursPerWeekGroups.SIXTEEN],
  [25, hoursPerWeekGroups.TWENTY_ONE],
  [30, hoursPerWeekGroups.TWENTY_SIX],
  [35, hoursPerWeekGroups.THIRTY_ONE],
  [40, hoursPerWeekGroups.THIRTY_SIX],
];

export const PositionUtil = {
  getRole: (position?: Partial<Position> | null): string => {
    return position?.role || '';
  },

  getWeeklyTimeCommitment: ({
    candidateInput,
    position,
  }: {
    candidateInput:
      Pick<Candidate, 'maxHours'> | { record: Pick<Candidate, 'maxHours'> };
    position?: Partial<Position> | null;
  }): 'Full-time' | 'Part-time' | null => {
    const candidate =
      'record' in candidateInput ? candidateInput.record : candidateInput;
    const maxHours = candidate.maxHours || 40;
    const { billingUnitsPerWeek, weeklyTimeUnit } = position || {};

    if (!weeklyTimeUnit) {
      return null;
    }

    if (
      weeklyTimeUnit === WeeklyTimeUnits.Hourly &&
      billingUnitsPerWeek != null &&
      billingUnitsPerWeek >= maxHours
    ) {
      return 'Full-time';
    }

    return weeklyTimeUnit === WeeklyTimeUnits.Daily &&
      billingUnitsPerWeek != null &&
      billingUnitsPerWeek >= 5
      ? 'Full-time'
      : 'Part-time';
  },

  getHoursPerWeek: (position?: Partial<Position> | null): string | null => {
    const billingHoursPerWeek = position?.billingHoursPerWeek;

    if (!billingHoursPerWeek) {
      return null;
    }

    const match = hoursPerWeekThresholds.find(
      ([max]) => billingHoursPerWeek <= max
    );
    return match ? match[1] : hoursPerWeekGroups.OVER_FORTY;
  },

  getEngagementLengthGrouped: (
    position?: Partial<Position> | null
  ): string | null => {
    const estimatedEngagementTotalMonths =
      position?.estimatedEngagementTotalMonths;

    if (estimatedEngagementTotalMonths == null) {
      return null;
    }

    if (estimatedEngagementTotalMonths <= 3) {
      return monthGroups.ZERO;
    } else if (estimatedEngagementTotalMonths <= 6) {
      return monthGroups.FOUR;
    } else if (estimatedEngagementTotalMonths <= 9) {
      return monthGroups.SEVEN;
    } else if (estimatedEngagementTotalMonths <= 12) {
      return monthGroups.TEN;
    }

    return monthGroups.OVER_TWELVE;
  },

  getEngagementLengthSingle: (
    position: Partial<Position> | null
  ): string | null => {
    const estimatedEngagementTotalMonths =
      position?.estimatedEngagementTotalMonths;

    if (estimatedEngagementTotalMonths == null) {
      return null;
    }

    const monthLength = Math.round(estimatedEngagementTotalMonths);

    return `${monthLength} ${PluralizeUtil({ count: monthLength, text: 'month' })}`;
  },

  getWorksite: (position?: Partial<Position> | null): string | null => {
    if (position?.worksite) {
      return position.worksite === PositionsConst.Worksites.OnsiteRemote
        ? 'Hybrid'
        : position.worksite;
    }
    return null;
  },
};
